import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import LaunchIcon from '@mui/icons-material/Launch'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import {
  selectSpecificationLoading,
  selectSpecifications,
} from '../../redux/specification/specificationSelectors'
import {
  selectAverageSCScore,
  selectRelatedAudits,
  selectSecurityControl,
  selectRelatedSecurityControls,
} from '../../redux/securityControls/securityControlsSelector'
import {
  fetchAuditsFromRelatedControls,
  fetchAverageSCScoreFromRelatedControls,
  getSecurityControlById,
  fetchSecurityControlsByLabels,
} from '../../redux/securityControls/securityControlsAction'
import { clearRelatedData } from '../../redux/securityControls/securityControlsSlice'
import {
  selectActions,
  selectLoading as selectActionsLoading,
} from '../../redux/actions/actionSelectors'
import {
  fetchActionsByRelatedControlId,
  getAExistingControlbySecurityControl,
} from '../../redux/actions/actionActions'
import useColumnsAction from '../../helpers/actionColumnsHelper'
import {
  selectRelatedRisks,
  selectRelatedRisksLoading,
} from '../../redux/risques/risqueSelectors'
import { fetchRisksFromRelatedControls } from '../../redux/risques/risqueActions'
import {
  selectRelatedIncidents,
  selectLoading as selectIncidentsLoading,
} from '../../redux/incidents/incidentSelectors'
import { fetchIncidentsFromRelatedControls } from '../../redux/incidents/incidentActions'
import useColumnsIncident from '../../helpers/incidentColumnsHelper'
import columnsRisk from '../../helpers/riskColumnsHelper'
import useColumnsAudit from '../../helpers/auditColumnsHelper'
import useExistingControlsColumns from '../../helpers/existingControlsColumnsHelper'
import { formatTitle } from '../../utils/formatTitle'
import { formatStructuredDescription } from '../../utils/formatStructuredDescription'
import { formatNormLabel, normToRelatedStandard } from '../../utils/normLabels'
import { writeSecurityNav } from '../../utils/securityNav'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import SourceTag from '../../components/common/SourceTag'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import TablePager from '../../components/common/TablePager'
import { useClientPagination } from '../../hooks/useClientPagination'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { onClickableTableRow } from '../../utils/tableRowClick'
import {
  DetailAccordion,
  DetailButton,
  DetailDescriptionPanel,
  DetailKpiCard,
  DetailKpiGrid,
  DetailPageHead,
} from '../../components/common/detail'
import { listPageIconBtnStyle } from '../../components/common/ListPageShell'
import MappingPaginatedGrid from './MappingPaginatedGrid'
import MappingFlowStepper from './MappingFlowStepper'
import '../../components/common/ListPageTable.css'
import '../securityFramework/SecurityFramework.css'
import './MappingFlow.css'
import { formatDecimal } from '../../utils/formatNumbers'

function RelatedDataMap() {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const actionColumns = useColumnsAction(undefined, true)
  const incidentColumns = useColumnsIncident(undefined, true)
  const auditColumns = useColumnsAudit(undefined, true)
  const { selectedRelated, id } = useParams<{
    selectedRelated: string
    id: string
  }>()
  const existingControlColumns = useExistingControlsColumns(
    id as string,
    undefined,
    true
  )

  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''
  const specsLoading = useSelector(selectSpecificationLoading)
  const securityControl = useSelector(selectSecurityControl)
  const securityLoading = useSelector(
    (state: RootState) => state.security.loading
  )
  const roles = useSelector((state: RootState) => state.auth.roles)
  const actions = useSelector(selectActions) || []
  const actionsLoading = useSelector(selectActionsLoading)
  const risks = useSelector(selectRelatedRisks) || []
  const risksLoading = useSelector(selectRelatedRisksLoading)
  const audits = useSelector(selectRelatedAudits) || []
  const scoreData = useSelector(selectAverageSCScore)
  const incidents = useSelector(selectRelatedIncidents) || []
  const incidentsLoading = useSelector(selectIncidentsLoading)
  const existingControlsList =
    useSelector((state: RootState) => state.action.existingControls) || []
  const relatedSecurityControls =
    useSelector(selectRelatedSecurityControls) || []

  const [relatedSettled, setRelatedSettled] = useState(false)

  useEffect(() => {
    dispatch(clearRelatedData())
    setRelatedSettled(false)
    if (id) {
      dispatch(getSecurityControlById(id))
      dispatch(fetchActionsByRelatedControlId(id))
      dispatch(fetchIncidentsFromRelatedControls(id))
      dispatch(fetchRisksFromRelatedControls(id))
      dispatch(fetchAuditsFromRelatedControls(id))
      dispatch(fetchAverageSCScoreFromRelatedControls(id))
      dispatch(getAExistingControlbySecurityControl(id))
    }
  }, [dispatch, id])

  const normalizedStandard = normToRelatedStandard(selectedNorm)
  const filteredRelated = useMemo(
    () =>
      securityControl?.RelatedControls?.filter(
    (item) => item.Standard === normalizedStandard
      ) || [],
    [normalizedStandard, securityControl]
  )
  const availableLabels = useMemo(
    () =>
      new Set(filteredRelated.flatMap((item) => item.ControlIds || [])).size,
    [filteredRelated]
  )

  useEffect(() => {
    if (!securityControl || !selectedNorm || specsLoading) return
    const labels = filteredRelated.flatMap((item) => item.ControlIds || [])
    if (labels.length === 0) {
      setRelatedSettled(true)
      return
    }
    let cancelled = false
    setRelatedSettled(false)
        dispatch(fetchSecurityControlsByLabels(labels))
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setRelatedSettled(true)
      })
    return () => {
      cancelled = true
    }
  }, [
    dispatch,
    filteredRelated,
    securityControl,
    selectedNorm,
    specsLoading,
  ])

  const relatedLoading =
    !securityControl || specsLoading || securityLoading || !relatedSettled
  const showRelatedLoader = useTableLoadingGate(relatedLoading, [id])
  const showActionsLoader = useTableLoadingGate(actionsLoading, [id])
  const showRisksLoader = useTableLoadingGate(risksLoading, [id])
  const showIncidentsLoader = useTableLoadingGate(incidentsLoading, [id])
  const showAuditsLoader = useTableLoadingGate(securityLoading, [id])
  const showExistingLoader = useTableLoadingGate(actionsLoading, [id])

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    related: true,
    tasks: false,
    risks: false,
    incidents: false,
    audits: false,
    existing: false,
  })
  const toggle = (section: string) =>
    setExpanded((current) => ({
      ...current,
      [section]: !current[section],
    }))

  const mappedCount = relatedSecurityControls.length
  const relatedPager = useClientPagination(relatedSecurityControls, 10)
  // Labels are mapping keys; resolved controls can exceed unique labels.
  const coverage = availableLabels > 0 ? Math.min(mappedCount, availableLabels) : 0
  const progress =
    availableLabels > 0
      ? Math.round((coverage / availableLabels) * 100)
      : mappedCount > 0
        ? 100
        : 0
  const averageScore =
    scoreData?.averageScore !== undefined
      ? formatDecimal(Number(scoreData.averageScore))
      : '—'

  const canViewAnalysis =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const sourceLabel = formatNormLabel(selectedRelated || '')
  const targetLabel = formatNormLabel(selectedNorm)
  const formattedDescription = formatStructuredDescription(
    securityControl?.Description
  )

  const openRelatedControl = (controlId: string) => {
    navigate(`/security-control-details/${controlId}`)
  }

  return (
    <div className="detail-page mapping-flow">
      <MappingFlowStepper
        currentStep={4}
        sourceNorm={selectedRelated}
        familyId={securityControl?.SecurityCheckFamilyId}
        controlId={id}
      />

      <DetailPageHead
        eyebrow={t('frameworkMappingPage.mappingDetailEyebrow')}
        title={formatTitle((securityControl?.Label as string) || '')}
        subtitle={t('frameworkMappingPage.mappingDetailSubtitle')}
        actions={
          canViewAnalysis ? (
            <DetailButton
              onClick={() => {
                const label = formatTitle(
                  (securityControl?.Label as string) || ''
                )
                writeSecurityNav({
                  norm: selectedRelated || undefined,
                  controlId: id,
                  controlLabel: label || undefined,
                  familyId: securityControl?.SecurityCheckFamilyId,
                })
                navigate(
                  `/related-controls/${selectedRelated}/view-analysis/${id}`,
                  {
                    state: {
                      controlLabel: label,
                      familyId: securityControl?.SecurityCheckFamilyId,
                    },
                  }
                )
              }}
            >
              <BarChartOutlined sx={{ fontSize: 16 }} />
              {t('buttons.viewAnalysis')}
            </DetailButton>
          ) : undefined
        }
      />

      <div className="mapping-flow-callout">
        <span className="mapping-flow-step">04</span>
        <div>
          <strong>{t('frameworkMappingPage.reviewMapping')}</strong>
          <p>{t('frameworkMappingPage.reviewMappingInstruction')}</p>
        </div>
        <div className="mapping-progress">
          <div className="mapping-progress-meta">
            <span>{t('frameworkMappingPage.mappingProgress')}</span>
            <span>
              {t('frameworkMappingPage.mappingProgressValue', {
                percent: progress,
                defaultValue: `${progress}%`,
              })}
            </span>
          </div>
          <div className="mapping-progress-track">
            <div
              className="mapping-progress-bar"
              style={{ width: `${progress}%` }}
            />
                </div>
          <p className="mapping-progress-hint">
            {t('frameworkMappingPage.mappedAvailable', {
              mapped: mappedCount,
              available: availableLabels,
            })}
                  </p>
                </div>
      </div>

      <div className="fw-source-bar">
        <div className="fw-stat">
          <div className="fw-stat-label">
            {t('frameworkMappingPage.sourceFramework')}
          </div>
          <div className="fw-stat-value">{sourceLabel}</div>
        </div>
        <div className="fw-stat-divider" />
        <div className="fw-stat">
          <div className="fw-stat-label">
            {t('frameworkMappingPage.targetFramework')}
          </div>
          <div className="fw-stat-value">{targetLabel}</div>
        </div>
        <div className="fw-stat-divider" />
        <div className="fw-stat">
          <div className="fw-stat-label">
            {t('Source', { defaultValue: 'Source' })}
          </div>
          <div className="fw-stat-value">
            {securityControl?.Source || '—'}
          </div>
        </div>
      </div>

      <DetailKpiGrid>
        <DetailKpiCard
          label={t('frameworkMappingPage.relatedControlsCount')}
          value={mappedCount}
          description={t('frameworkMappingPage.labelsInMapping', {
            count: availableLabels,
            defaultValue: `${availableLabels} labels in mapping`,
          })}
        />
        <DetailKpiCard
          label={t('controleSecurityDetails.accordion.averageScoreKpi')}
          value={averageScore}
        />
        <DetailKpiCard label={t('tasks')} value={actions.length} />
        <DetailKpiCard label={t('risks')} value={risks.length} />
        <DetailKpiCard label={t('incidents')} value={incidents.length} />
        <DetailKpiCard label={t('audits')} value={audits.length} />
        <DetailKpiCard
          label={t('controleSecurityDetails.accordion.existingControl')}
          value={existingControlsList.length}
        />
      </DetailKpiGrid>

      <DetailDescriptionPanel
        title={t('Description')}
        subtitle={t('securityControlDetailsPage.descriptionSubtitle')}
        text={formattedDescription}
        emptyLabel={t('securityControlDetailsPage.noDescription')}
      />

      <DetailAccordion
        title={`${t('controleSecurityDetails.accordion.relatedControls')} · ${targetLabel}`}
        count={mappedCount}
        open={expanded.related}
        onToggle={() => toggle('related')}
      >
        <div className="incyia-table-wrap">
          {showRelatedLoader ? (
            <TableLoadingState compact />
          ) : relatedSecurityControls.length === 0 ? (
            <TableEmptyState compact />
          ) : (
            <>
              <table className="incyia-table">
                <thead>
                  <tr>
                    <th>{t('Label')}</th>
                    <th>{t('Description')}</th>
                    <th>{t('Source')}</th>
                    <th>{t('listPages.score')}</th>
                    <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedPager.pageItems.map((control: any) => {
                    const controlId = control._id as string
                    const score =
                      control.SCScore != null &&
                      !Number.isNaN(Number(control.SCScore))
                        ? formatDecimal(Number(control.SCScore))
                        : '—'

                    return (
                      <tr key={controlId} onClick={onClickableTableRow}>
                        <td className="incyia-col-pinned">
                          <button
                            type="button"
                            className="incyia-name-link"
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                            onClick={() => openRelatedControl(controlId)}
                          >
                            <ListPageDescription
                              value={
                                control.Label
                                  ? formatTitle(control.Label)
                                  : undefined
                              }
                              tone="primary"
                            />
                          </button>
                        </td>
                        <td className="incyia-wrap">
                          <ListPageDescription value={control.Description} />
                        </td>
                        <td>
                          <SourceTag value={control.Source} />
                        </td>
                        <td>
                          <span className="incyia-money">{score}</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="incyia-row-actions">
                            <button
                              type="button"
                              title={t('listPages.openAction')}
                              onClick={() => openRelatedControl(controlId)}
                              style={listPageIconBtnStyle}
                            >
                              <LaunchIcon sx={{ fontSize: 15 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {relatedPager.showPager ? (
                <TablePager
                  page={relatedPager.page}
                  pageSize={relatedPager.pageSize}
                  total={relatedPager.total}
                  pageCount={relatedPager.pageCount}
                  from={relatedPager.from}
                  to={relatedPager.to}
                  pageSizeOptions={relatedPager.pageSizeOptions}
                  onPageChange={relatedPager.setPage}
                  onPageSizeChange={relatedPager.setPageSize}
                />
              ) : null}
            </>
                )}
              </div>
      </DetailAccordion>
      <DetailAccordion
        title={`${t('controleSecurityDetails.accordion.relatedActions')} · ${targetLabel}`}
        count={actions.length}
        open={expanded.tasks}
        onToggle={() => toggle('tasks')}
      >
        <MappingPaginatedGrid
          rows={actions}
          columns={actionColumns}
                    getRowId={(row) => row._id}
          loading={showActionsLoader}
        />
      </DetailAccordion>
      <DetailAccordion
        title={`${t('controleSecurityDetails.accordion.relatedRisks')} · ${targetLabel}`}
        count={risks.length}
        open={expanded.risks}
        onToggle={() => toggle('risks')}
      >
        <MappingPaginatedGrid
                    rows={risks}
                    columns={columnsRisk(undefined, undefined, undefined, true)}
                    getRowId={(row) => row._id}
          loading={showRisksLoader}
        />
      </DetailAccordion>
      <DetailAccordion
        title={`${t('controleSecurityDetails.accordion.relatedIncidents')} · ${targetLabel}`}
        count={incidents.length}
        open={expanded.incidents}
        onToggle={() => toggle('incidents')}
      >
        <MappingPaginatedGrid
          rows={incidents}
          columns={incidentColumns}
          getRowId={(row) => row._id}
          loading={showIncidentsLoader}
        />
      </DetailAccordion>
      <DetailAccordion
        title={`${t('controleSecurityDetails.accordion.relatedAudits')} · ${targetLabel}`}
        count={audits.length}
        open={expanded.audits}
        onToggle={() => toggle('audits')}
      >
        <MappingPaginatedGrid
                    rows={audits}
          columns={auditColumns}
                    getRowId={(row) => row._id}
          loading={showAuditsLoader}
        />
      </DetailAccordion>
      <DetailAccordion
        title={t('controleSecurityDetails.accordion.existingControl')}
        count={existingControlsList.length}
        open={expanded.existing}
        onToggle={() => toggle('existing')}
      >
        <MappingPaginatedGrid
                    rows={existingControlsList}
          columns={existingControlColumns}
                    getRowId={(row) => row._id}
          loading={showExistingLoader}
        />
      </DetailAccordion>
      </div>
  )
}

export default RelatedDataMap
